export type PleskProvisionResult = {
  ok?: boolean
  error?: string
  status?: string
  pipeline?: PipelineLog[]
  diagnostics?: {
    stage?: string
    status?: number
    detail?: string
    response?: string
  }
}

export type PipelineLog = {
  step: string
  status: 'ok' | 'warn' | 'error'
  message: string
  ts: string
}

export type VerifyResult = {
  overall: 'pass' | 'partial' | 'fail'
  domain: string
  checks: { name: string; status: 'pass' | 'fail' | 'warn'; detail: string }[]
  summary: string
}

function extractDetailMessage(detail?: string) {
  if (!detail) return null

  try {
    const parsed = JSON.parse(detail) as { message?: string }
    return typeof parsed?.message === "string" ? parsed.message : detail
  } catch {
    return detail
  }
}

export function getPleskProvisionErrorMessage(response: unknown): string | null {
  if (!response || typeof response !== "object") return null

  const result = response as PleskProvisionResult
  if (result.ok !== false && !result.error) return null

  const detailMessage = extractDetailMessage(result.diagnostics?.detail)

  if (result.diagnostics?.stage === "plesk_auth") {
    return "Plesk login failed. Check the Plesk username and password."
  }

  if (result.diagnostics?.stage === "domain_creation" && result.diagnostics?.status === 409) {
    return detailMessage || "This domain already exists in Plesk."
  }

  return detailMessage || result.error || "Deployment failed."
}

export function assertPleskProvisionSucceeded(response: unknown) {
  const message = getPleskProvisionErrorMessage(response)

  if (message) {
    throw new Error(message)
  }
}

export function getPipelineLogs(response: unknown): PipelineLog[] {
  if (!response || typeof response !== "object") return []
  const result = response as PleskProvisionResult
  return result.pipeline || []
}

export function getPipelineStatus(response: unknown): string {
  if (!response || typeof response !== "object") return 'unknown'
  const result = response as Record<string, any>
  return result.status || 'unknown'
}
